import React, { useMemo, useState } from 'react';
import {
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useGetAvailableTenantsQuery, useSwitchTenantMutation } from '@/services/adminApi';
import { sfAuth } from '@/auth/SFAuth';

interface TenantSwitcherProps {
  currentTenantId: string;
  onTenantChange?: (tenantId: string) => void;
}

const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ currentTenantId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isSwitching, setIsSwitching] = useState(false);
  const [search, setSearch] = useState('');

  const {
    data: tenants,
    isLoading,
    error: loadError,
  } = useGetAvailableTenantsQuery(undefined, {
    // Only fetch when the menu opens; keeps initial-page render lean.
    skip: !open,
  });
  const [switchTenant] = useSwitchTenantMutation();

  const visibleTenants = useMemo(() => {
    if (!tenants) {
      return [];
    }
    const sorted = [...tenants].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    const term = search.trim().toLowerCase();
    if (!term) {
      return sorted;
    }
    return sorted.filter(
      (t) => t.name.toLowerCase().includes(term) || t.id.toLowerCase().includes(term),
    );
  }, [tenants, search]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleSelect = async (tenantId: string) => {
    handleClose();

    setIsSwitching(true);
    try {
      const payload = await switchTenant({ tenantId }).unwrap();
      if (!payload.success) {
        const message = payload.errors?.[0]?.message ?? 'Tenant switch failed. Please try again.';
        alert(message);
        setIsSwitching(false);
        return;
      }
      // Force a token refresh to pick up the new tenant claim, then reload
      await sfAuth.getAccessToken(true);
      window.location.reload();
    } catch (err) {
      setIsSwitching(false);
      alert(err instanceof Error ? err.message : 'Tenant switch failed. Please try again.');
    }
  };

  return (
    <>
      <Chip
        label={isSwitching ? 'Switching…' : `TENANT: ${currentTenantId}`}
        color="error"
        variant="filled"
        size="medium"
        onClick={isSwitching ? undefined : handleClick}
        disabled={isSwitching}
        sx={{
          mr: 2,
          fontWeight: 'bold',
          fontSize: '0.95rem',
          cursor: 'pointer',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.4)' },
            '70%': { boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)' },
          },
          '&:hover': { backgroundColor: '#d32f2f' },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // Prevent Menu from auto-focusing the first item so the search field can take focus
        // and so single-key presses don't get hijacked by Menu's type-to-focus behavior.
        autoFocus={false}
        disableAutoFocusItem
        slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 480 } } }}
      >
        <Box key="header" sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Select Tenant
          </Typography>
        </Box>
        <Box key="search" sx={{ px: 2, py: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Filter tenants…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            // Stop key events from bubbling to Menu so its type-ahead / arrow-nav doesn't
            // hijack focus from this field. Escape still bubbles so the menu can close.
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Divider key="divider" />

        {isLoading && (
          <Box key="loading" sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {loadError && (
          <Box key="error" sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" color="error">
              Failed to load tenants
            </Typography>
          </Box>
        )}

        {!isLoading && !loadError && visibleTenants.length === 0 && (
          <Box key="no-tenants" sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {tenants && tenants.length > 0 ? 'No matching tenants' : 'No tenants available'}
            </Typography>
          </Box>
        )}

        {visibleTenants.map((tenant) => (
          <MenuItem
            key={tenant.id}
            onClick={() => handleSelect(tenant.id)}
            selected={tenant.id === currentTenantId}
            sx={{ '&.Mui-selected': { backgroundColor: 'action.selected' } }}
          >
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {tenant.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {tenant.id}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TenantSwitcher;
